import React from 'react';
import { ArrowRightIcon, CheckIcon, MinusIcon } from 'lucide-react';
import { LandingHeader } from '../components/LandingHeader';
import { MobileComparison } from '../components/pricing/MobileComparison';
import { comparisonRows } from '../data/pricingComparison';
import { formatPrice, pricingPlans } from '../data/pricingPlans';
function planHref(planId: string) {
  return planId === 'caas' ? 'mailto:help@creditmanager.uk.com?subject=Credit%20Control%20as%20a%20Service' : `/signup?plan=${planId}`;
}
function planCta(planId: string) {
  return planId === 'caas' ? 'Enquire' : 'Start free trial';
}
function ComparisonValue({
  value
}: {
  value: string | boolean;
}) {
  if (value === true) {
    return <>
        <CheckIcon size={17} className="mx-auto text-[#5B5299]" aria-hidden="true" data-id="element-853" data-mp="stc6tle5dsz2" />
        <span className="sr-only" data-id="element-854" data-mp="1giwpe36e2o7t">Included</span>
      </>;
  }
  if (value === false) {
    return <>
        <MinusIcon size={16} className="mx-auto text-stone-300" aria-hidden="true" data-id="element-855" data-mp="1lcsoxa2awj53" />
        <span className="sr-only" data-id="element-856" data-mp="1e14ie56vbgkm">Not included</span>
      </>;
  }
  return <span className="text-sm font-semibold text-[#041F60]" data-id="element-857" data-mp="1dz1nb6quyx91">{value}</span>;
}
export function PricingPage() {
  return <div id="top" className="min-h-screen w-full bg-white text-[#041F60]" data-id="element-858" data-mp="1nzbyymx8vwm3">
      <a href="#main-content" className="sr-only fixed left-4 top-4 z-50 rounded-lg bg-[#041F60] px-4 py-2 text-sm font-bold text-white focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-[#4BE4FF]" data-id="element-859" data-mp="1nx7m3fhdbn9o">
        Skip to content
      </a>
      <LandingHeader current="/pricing" data-id="element-860" data-mp="2bnbmldz9f6wg" />

      <main id="main-content" data-id="element-861" data-mp="26pjy6txtq2x0">
        <section className="border-b border-[#A6B6F8]/50 bg-[#A6B6F8]/20" data-id="element-862" data-mp="26pa8l7goxxru">
          <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8 sm:py-20" data-id="element-863" data-mp="hac339rrdsjw">
            <div className="mx-auto max-w-2xl text-center" data-id="element-864" data-mp="hcq03o601eu3">
              <h1 className="font-display text-3xl font-bold uppercase leading-tight tracking-[0.02em] sm:text-[2.6rem]" data-id="element-865" data-mp="ccu4odbdavq5">
                Pricing
              </h1>
              <p className="mt-5 text-base leading-7 text-[#3A3A3A] sm:text-lg" data-id="element-866" data-mp="160zv4lhec568">
                Our virtual credit packages start from £45 a month. Every plan
                includes a free trial from day one and runs for a 12-month
                contract period, billed monthly.
              </p>
            </div>

            <ul className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-id="element-867" data-mp="1kzbtcwkf3lzi">
              {pricingPlans.map(plan => <li key={plan.id} className="h-full" data-id="element-868" data-mp="1oykn41fgjjv2">
                  <article className={`flex h-full flex-col bg-white p-6 sm:p-7 ${plan.recommended ? 'border-2 border-[#041F60]' : 'border border-stone-200'}`} data-id="element-869" data-mp="1tr42n3xpgp2i">
                    <div className="flex min-h-6 items-start justify-between gap-2" data-id="element-870" data-mp="28nv37gj486tp">
                      <h2 className="font-display text-sm font-bold uppercase tracking-[0.1em] text-[#5B5299]" data-id="element-871" data-mp="28lqrwdiw05je">
                        {plan.name}
                      </h2>
                      {plan.recommended && <span className="shrink-0 bg-[#4BE4FF] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#041F60]" data-id="element-872" data-mp="3j7g0z6di9uae">
                          Most popular
                        </span>}
                    </div>

                    <p className="mt-4 flex items-baseline gap-2" data-id="element-873" data-mp="ftbvw8ab6yhy">
                      <span className="text-4xl font-bold tracking-[-0.04em] text-[#041F60]" data-id="element-874" data-mp="ath5e5ug4axf">
                        {plan.priceLabel || formatPrice(plan.monthlyPrice)}
                      </span>
                      <span className="text-xs font-semibold text-stone-500" data-id="element-875" data-mp="11l9n1fu0wwd">
                        / month
                      </span>
                    </p>
                    <p className="mt-3 text-sm leading-6 text-[#3A3A3A]" data-id="element-876" data-mp="tra9hbgt2qwe">
                      {plan.tagline}
                    </p>

                    <ul className="mt-5 space-y-2.5 border-t border-stone-200 pt-5" data-id="element-877" data-mp="e87o7e1juajb">
                      {plan.features.map(feature => <li key={feature} className="flex gap-2.5 text-sm leading-6 text-[#3A3A3A]" data-id="element-878" data-mp="j5r38g62mrcb">
                          <CheckIcon size={15} className="mt-1 shrink-0 text-[#5B5299]" aria-hidden="true" data-id="element-879" data-mp="9a61mj4wzt33" />
                          {feature}
                        </li>)}
                    </ul>

                    <a href={planHref(plan.id)} className={`font-display mt-auto flex items-center justify-center gap-2 px-4 py-3.5 text-[12px] font-bold uppercase tracking-[0.11em] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 ${plan.recommended ? 'bg-[#041F60] text-white hover:bg-[#031849] focus:ring-[#041F60]' : 'border border-[#041F60] text-[#041F60] hover:bg-[#041F60] hover:text-white focus:ring-[#041F60]'}`} style={{
                  marginTop: '2rem'
                }} data-id="element-880" data-mp="s8idwdr4hj2w">
                      {planCta(plan.id)}
                      <ArrowRightIcon size={16} aria-hidden="true" data-id="element-881" data-mp="2mg465szi8dsh" />
                    </a>
                  </article>
                </li>)}
            </ul>

          </div>
        </section>

        <section className="bg-white" data-id="element-882" data-mp="2vhkd1qr41xry">
          <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20" data-id="element-883" data-mp="2vin6a5bwocb1">
            <div className="mx-auto max-w-2xl text-center" data-id="element-884" data-mp="2rexhpfv8duq0">
              <h2 className="font-display text-2xl font-bold uppercase leading-tight tracking-[0.02em] sm:text-[2.1rem]" data-id="element-885" data-mp="aqtc8e7us52r">
                Compare the plans
              </h2>
              <p className="mt-4 text-base leading-7 text-[#3A3A3A]" data-id="element-886" data-mp="6rkn5mnrcvdj">
                Every plan includes the debtor dashboard, unlimited users and
                unlimited credit alerts. The differences are in chasing and
                credit report volume.
              </p>
            </div>

            <MobileComparison planHref={planHref} planCta={planCta} data-id="element-887" data-mp="39g1o999ydbre" />

            <div className="mt-10 hidden overflow-x-auto lg:block" data-id="element-888" data-mp="3dj7o9z6h26pf">
              <table className="w-full min-w-[820px] border-collapse text-left" data-id="element-889" data-mp="2gu9sqeqwe4r7">
                <caption className="sr-only" data-id="element-890" data-mp="2lsbn3vtrxqhz">
                  Feature comparison across the Basic, Advanced, Premium and
                  Credit Control as a Service plans.
                </caption>
                <thead data-id="element-891" data-mp="2ukn2mmncntj6">
                  <tr className="border-b-2 border-[#041F60]" data-id="element-892" data-mp="2ulx1he2115qv">
                    <th scope="col" className="w-[34%] py-4 pr-4 align-bottom text-xs font-bold uppercase tracking-[0.14em] text-[#5B5299]" data-id="element-893" data-mp="1y2z4648na42h">
                      Feature
                    </th>
                    {pricingPlans.map(plan => <th key={plan.id} scope="col" className="px-3 py-4 align-bottom text-center" data-id="element-894" data-mp="1plgoyg31x0dn">
                        <span className="block text-xs font-bold uppercase tracking-[0.08em] text-[#5B5299]" data-id="element-895" data-mp="1glk3enbykhmt">
                          {plan.name}
                        </span>
                        <span className="mt-1.5 block text-lg font-bold tracking-tight text-[#041F60]" data-id="element-896" data-mp="1bo1rqp6k3ob4">
                          {plan.priceLabel || formatPrice(plan.monthlyPrice)}
                          <span className="text-xs font-semibold text-stone-500" data-id="element-897" data-mp="11ubaa48uke4x">
                            {' '}
                            / mo
                          </span>
                        </span>
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-898" data-mp="nypl9x2m5uci">
                  {comparisonRows.map((row, index) => <tr key={row.feature} className="border-b border-[#A6B6F8]/40" style={{
                  backgroundColor: index % 2 === 1 ? 'rgba(166,182,248,0.08)' : 'transparent'
                }} data-id="element-899" data-mp="2bu3yfiwj4nz3" data-mpi={index}>
                      <th scope="row" className="py-3.5 pr-4 text-sm font-semibold text-[#041F60]" data-id="element-900" data-mp="1t19url4cwia6">
                        {row.feature}
                      </th>
                      {row.values.map((value, valueIndex) => <td key={pricingPlans[valueIndex].id} className="px-3 py-3.5 text-center" data-id="element-901" data-mp="1o49lz2g4h1fd" data-mpi={valueIndex}>
                          <ComparisonValue value={value} data-id="element-902" data-mp="1f20d0h0i5g43" />
                        </td>)}
                    </tr>)}
                  <tr data-id="element-903" data-mp="vj33fg6wtzxe">
                    <td className="py-6 pr-4" data-id="element-904" data-mp="qiow60d4uu0f" />
                    {pricingPlans.map(plan => <td key={plan.id} className="px-3 py-6 text-center" data-id="element-905" data-mp="15fz6v7hybvv3">
                        <a href={planHref(plan.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#041F60] px-3.5 py-2.5 text-xs font-bold text-[#041F60] transition hover:bg-[#041F60] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#041F60] focus:ring-offset-2" data-id="element-906" data-mp="10gcsguqe09kp">
                          {planCta(plan.id)}
                        </a>
                      </td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="navy-texture border-t border-[#031849] text-white" data-id="element-907" data-mp="1t5ab4yyig8dz">
          <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 px-5 py-14 sm:px-8 sm:py-20 md:flex-row md:items-center" data-id="element-908" data-mp="1t37dbsr49ayp">
            <div className="max-w-xl" data-id="element-909" data-mp="128v4avuwac5v">
              <h2 className="font-display text-2xl font-bold uppercase leading-tight tracking-[0.02em] sm:text-[2.1rem]" data-id="element-910" data-mp="127ts2o257jmk">
                Get started now
              </h2>
              <p className="mt-3 text-base leading-7 text-[#A6B6F8]" data-id="element-911" data-mp="sd1x7rzy3avh">
                Start your free trial and bring credit, collections and invoice
                finance into one practical view.
              </p>
            </div>
            <a href="/signup" className="font-display inline-flex shrink-0 items-center gap-2 bg-[#4BE4FF] px-9 py-4 text-[13px] font-bold uppercase tracking-[0.13em] text-[#041F60] transition-colors duration-150 hover:bg-[#38d9f2] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#041F60]" data-id="element-912" data-mp="1g11kghy1sj5b">
              Start your free trial{' '}
              <ArrowRightIcon size={16} aria-hidden="true" data-id="element-913" data-mp="2c7wkk38bzjca" />
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-stone-200 bg-white" data-id="element-914" data-mp="2g8gih90qaqzh">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-5 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8" data-id="element-915" data-mp="2l8b715x174hd">
          <p className="text-sm font-semibold text-[#041F60]" data-id="element-916" data-mp="2pzb205mlbch8">Credit Manager</p>
          <a href="mailto:help@creditmanager.uk.com" className="text-sm font-semibold text-[#5B5299] underline decoration-[#A6B6F8] underline-offset-4 transition hover:text-[#041F60] focus:text-[#041F60] focus:outline-none" data-id="element-917" data-mp="2uye87teb3oqs">
            help@creditmanager.uk.com
          </a>
          <p className="text-xs text-stone-500" data-id="element-918" data-mp="38yf938ukmw69">© 2026 Credit Manager</p>
        </div>
      </footer>
    </div>;
}